// 같은 건 합치기 창 (2026-11-03).
//
// 오너 — "앱을 통해 리포트가 넘어와서 고객사 리포트에 등록이 됐다 쳐. 그런데 필드팀 방문 필요 건이여서
// 필드팀이 캘린더에 방문 일정을 만들어. 그러면 중복된 리포트가 등록이 되는데, 리포트 넘버는 2개가 되잖아".
//
// 일정을 만들 때 '기존 리포트에 잇기'를 고르면 애초에 둘이 되지 않는다. 그래도 순서가 거꾸로면(방문 일정을
// 먼저 잡고 앱 리포트가 나중에 들어오면) 둘이 된다 — 그때 둘을 합친다.
// 오너 — "합칠때는 백넘버가 사라져야돼. 01이랑 07을 합치면 07이 없어져야하는거지". 먼저 등록된 번호가 남고
// 나중 번호가 사라진다(어느 쪽에서 열었는지와 상관없다 — 서버가 같은 셈으로 정한다).
// 리포트 연결 창(ReportLinkPicker)과 같은 '고르고 → 확인하고 → 합친다' 흐름이다.

import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  kstDateTime,
  SERVICE_ORDER_STATUS_LABELS,
  type CustomerSheetReportDto,
} from '@bhops/shared';
import { customerSheetReportsApi } from '@/api/customerSheetReports';
import { visitLabel } from '@/lib/report-visit';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import { useBackdropDismiss } from '@/components/use-backdrop-dismiss';
import { pill, SHEET_STATUS_TONE, SHEET_STATUS_TONE_UNKNOWN } from './field-tones';

const control: CSSProperties = {
  padding: '6px 8px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

/** 서버 report-match 와 같은 셈 — 괄호 안 글 · 빈칸 · 기호를 걷어 낸다. 같은 매장을 위로 올릴 때만 쓴다. */
const squash = (s: string | null | undefined) =>
  (s ?? '')
    .replace(/\[[^\]]*\]|\([^)]*\)/g, '')
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toLowerCase();

function sameStore(a: string | null, b: string | null) {
  const x = squash(a);
  const y = squash(b);
  return x.length >= 2 && y.length >= 2 && (x.includes(y) || y.includes(x));
}

/** 먼저 등록된 쪽인가 — 서버 merge 와 같은 셈(등록 시각, 같으면 번호 순). */
function registeredFirst(a: CustomerSheetReportDto, b: CustomerSheetReportDto) {
  const d = Date.parse(a.createdAt) - Date.parse(b.createdAt);
  return d !== 0 ? d < 0 : a.sheetRowKey.localeCompare(b.sheetRowKey) <= 0;
}

function Row({ r, tag }: { r: CustomerSheetReportDto; tag?: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {tag && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{tag}</span>}
        <span style={{ fontWeight: 600, color: 'var(--fg-brand)' }}>{r.sheetRowKey}</span>
        <span>{r.storeName?.trim() || '–'}</span>
        <span style={{ ...pill, ...(SHEET_STATUS_TONE[r.status] ?? SHEET_STATUS_TONE_UNKNOWN) }}>
          {SERVICE_ORDER_STATUS_LABELS[r.status]}
        </span>
        {r.fromCalendar && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>캘린더로 만든 리포트</span>
        )}
        {r.visit && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {visitLabel(r.visit)}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          {kstDateTime(r.receivedAt) ?? '–'}
        </span>
      </span>
      <span style={{ fontSize: 13 }}>{r.summary || '요약 없음'}</span>
    </span>
  );
}

export function MergeReportPicker({
  source,
  onClose,
}: {
  /** 이 창을 연 리포트. 남을지 사라질지는 고른 리포트와의 등록 순서가 정한다. */
  source: CustomerSheetReportDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [picked, setPicked] = useState<CustomerSheetReportDto | null>(null);

  // 처리 안 된 리포트만 — 해결된 건에 방문 일정을 옮길 일은 없다. 같은 매장은 위로 올린다.
  const listQ = useQuery({
    queryKey: ['merge-report-picker', applied],
    queryFn: () =>
      customerSheetReportsApi.list({ search: applied || undefined, open: true, pageSize: 50 }),
  });
  const items = useMemo(() => {
    const rows = (listQ.data?.items ?? []).filter((r) => r.id !== source.id);
    const near = rows.filter((r) => sameStore(r.storeName, source.storeName));
    // 같은 매장을 위로, 그 안에서도 고를 수 있는 것(방문 일정이 겹치지 않는 것)을 위로.
    const blocked1 = (r: CustomerSheetReportDto) => Number(!!source.visit && !!r.visit);
    return [...near, ...rows.filter((r) => !near.includes(r))].sort(
      (a, b) => blocked1(a) - blocked1(b),
    );
  }, [listQ.data, source]);

  const mergeMutation = useMutation({
    mutationFn: (target: CustomerSheetReportDto) =>
      customerSheetReportsApi.merge(source.id, target.id),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['customer-sheet-reports'] });
      void qc.invalidateQueries({ queryKey: ['fault-remediations'] });
      void qc.invalidateQueries({ queryKey: ['robot-sheet-reports'] });
      void qc.invalidateQueries({ queryKey: ['robot-fault-remediations'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        `${res.removedReportNo} 리포트를 ${res.target.sheetRowKey} 리포트에 합쳤습니다.${
          res.movedRemediations > 0 ? ` 조치 이력 ${res.movedRemediations}건도 옮겼습니다.` : ''
        }`,
        '합치기 완료',
      );
      onClose();
    },
    onError: (e) => toast.error(extractApiError(e), '합치기 실패'),
  });

  const busy = mergeMutation.isPending;
  const pair = picked
    ? registeredFirst(source, picked)
      ? { keep: source, drop: picked }
      : { keep: picked, drop: source }
    : null;
  const blocked = (r: CustomerSheetReportDto) => !!source.visit && !!r.visit;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="같은 건으로 합치기"
      {...useBackdropDismiss(onClose)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          padding: 20,
          width: 640,
          maxWidth: '92vw',
          maxHeight: '86vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h2 className="kuun-h3" style={{ margin: 0 }}>
          {picked ? '합치기 확인' : '같은 건으로 합치기'}
        </h2>

        {picked && pair ? (
          <>
            <div style={box} data-testid="merge-confirm-drop">
              <Row r={pair.drop} tag="지울 리포트" />
            </div>
            <div
              style={{ ...box, borderColor: 'var(--fg-brand)' }}
              data-testid="merge-confirm-target"
            >
              <Row r={pair.keep} tag="남길 리포트" />
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
              먼저 등록된 {pair.keep.sheetRowKey} 리포트를 남기고 {pair.drop.sheetRowKey} 리포트는
              목록에서 지웁니다. {pair.drop.visit ? `${visitLabel(pair.drop.visit)}과 ` : ''}조치
              이력은 {pair.keep.sheetRowKey} 리포트로 옮깁니다.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setPicked(null)}
                disabled={busy}
                style={{ ...control, cursor: 'pointer' }}
              >
                다시 고르기
              </button>
              <button
                type="button"
                onClick={() => mergeMutation.mutate(picked)}
                disabled={busy}
                data-testid="merge-confirm"
                style={{
                  ...control,
                  background: 'var(--fg-brand)',
                  color: 'var(--bg-surface)',
                  border: '1px solid var(--fg-brand)',
                  fontWeight: 600,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                {busy ? '합치는 중…' : '합치기'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={box}>
              <Row r={source} tag="이 리포트" />
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
              같은 신고인 리포트를 고르면 먼저 등록된 번호 하나만 남기고 나중 번호는 지웁니다. 같은
              매장 리포트가 위에 나옵니다.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setApplied(search.trim());
              }}
              style={{ display: 'flex', gap: 8 }}
            >
              <input
                style={{ ...control, flex: 1 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="요약, 매장, 리포트 번호로 검색"
                aria-label="리포트 검색"
              />
              <button type="submit" style={{ ...control, cursor: 'pointer' }}>
                검색
              </button>
            </form>

            {listQ.isLoading ? (
              <p style={muted}>불러오는 중…</p>
            ) : listQ.isError ? (
              <p style={{ ...muted, color: 'var(--fg-danger)' }}>리포트를 불러오지 못했습니다.</p>
            ) : items.length === 0 ? (
              <p style={muted}>처리 안 된 리포트 중 맞는 것이 없습니다.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {items.map((r) => {
                  const no = blocked(r);
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setPicked(r)}
                        disabled={no}
                        title={
                          no
                            ? `${r.sheetRowKey}에도 ${r.visit ? visitLabel(r.visit) : '방문 일정'}이 있어 합칠 수 없습니다. 한쪽 일정을 지운 뒤 합쳐 주세요.`
                            : undefined
                        }
                        data-testid={`merge-option-${r.sheetRowKey}`}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--border-subtle)',
                          padding: '10px 4px',
                          cursor: no ? 'not-allowed' : 'pointer',
                          opacity: no ? 0.55 : 1,
                          fontSize: 13,
                          color: 'var(--fg-default)',
                        }}
                      >
                        <Row r={r} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={{ ...control, cursor: 'pointer' }}>
                취소
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const box: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 12,
  background: 'var(--bg-subtle)',
};
const muted: CSSProperties = { fontSize: 13, color: 'var(--text-secondary)' };
